import { useEffect, useState } from 'react';
import { getFormsPendingApproval, approveForm, rejectForm, getForm } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const Approvals = () => {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [comment, setComment] = useState('');
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const data = await getFormsPendingApproval();
      setForms(data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (form, actionType) => {
    setSelectedForm(form);
    setAction(actionType);
    setComment('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (action === 'approve') {
        await approveForm(selectedForm.id, comment);
        toast.success('Formular genehmigt');
      } else {
        if (!comment.trim()) {
          toast.error('Kommentar ist erforderlich bei Ablehnung');
          return;
        }
        await rejectForm(selectedForm.id, comment);
        toast.success('Formular abgelehnt');
      }
      setDialogOpen(false);
      loadForms();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Genehmigungen</h1>
        <p className="text-gray-500 mt-1">Prüfen und genehmigen Sie Formulare von Benutzern</p>
        <p className="text-xs text-gray-400 mt-1">
          Hinweis: Super Admins, Abteilungsleiter und Stellvertreter können Formulare direkt veröffentlichen (Rollen-Override).
        </p>
      </div>

      {forms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Keine ausstehenden Genehmigungen</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {forms.map((form) => (
            <Card key={form.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{form.name}</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {form.title || 'Kein Titel'}
                    </p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600">
                      <span>Von: {form.created_by_name}</span>
                      <span>Abteilung: {form.department_name}</span>
                      <span>Felder: {form.fields.length}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleAction(form, 'reject')}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Ablehnen
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleAction(form, 'approve')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Genehmigen
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {form.description && (
                <CardContent>
                  <p className="text-sm text-gray-600">{form.description}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' ? 'Formular genehmigen' : 'Formular ablehnen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {selectedForm?.name}
            </p>
            <div>
              <Label>Kommentar {action === 'reject' && '(erforderlich)'}*</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ihr Kommentar zur Entscheidung..."
                rows={4}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button
                onClick={handleSubmit}
                className={action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {action === 'approve' ? 'Genehmigen' : 'Ablehnen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Approvals;